import { Form, Input } from 'antd'

import { API } from '@/api'
import { Button, message, Modal, Trans } from '@/components'
import { useUserStore } from '@/stores/user'

interface EditSshPubKeyModal {
  isEdit: boolean
  onClose: () => void
}

export function EditSshPubKeyModal(props: EditSshPubKeyModal) {
  const userStore = useUserStore()
  const { isEdit, onClose } = props
  const [form] = Form.useForm<{ public_key: string }>()

  const sshKeyPattern =
    /^(ssh-dss|ssh-rsa|ecdsa-sha2-nistp256|ecdsa-sha2-nistp384|ecdsa-sha2-nistp521|ssh-ed25519)\s+[A-Za-z0-9+/]+=*\s*.*$/

  const sshKeyRegex =
    /^(?<algo>ssh-(?:rsa|dss|ed25519)|ecdsa-sha2-nistp(?:256|384|521)) (?<key>[A-Za-z0-9+/=]+)(?: (?<comment>.*))?$/

  const keyLengthRules: Record<string, { min: number; max: number }> = {
    'ssh-ed25519': { min: 68, max: 68 },
    'ssh-rsa': { min: 200, max: 1000 },
    'ssh-dss': { min: 500, max: 800 },
    'ecdsa-sha2-nistp256': { min: 120, max: 160 },
    'ecdsa-sha2-nistp384': { min: 170, max: 210 },
    'ecdsa-sha2-nistp521': { min: 230, max: 270 },
  }

  const validateSshKeyLength = (_: any, value: string) => {
    const match = value.trim().match(sshKeyRegex)
    if (!match || !match.groups) {
      // throw new Error('SSH public key format is invalid.')
      return false
    }

    const { algo, key } = match.groups
    const rule = keyLengthRules[algo]
    if (!rule) {
      // throw new Error('Unsupported SSH key algorithm.')
      return false
    }

    if (key.length < rule.min || key.length > rule.max) {
      // throw new Error(
      //   `${algo} key length should be between ${rule.min} and ${rule.max} characters.`,
      // )
      return false
    }

    return true
  }

  const onOK = async () => {
    try {
      const values = await form.validateFields()
      if (isEdit) {
        await API.deleteUserSshPubKey()
      }
      await API.addUserSshPubKey({ public_key: values.public_key })
      userStore.getSshPubKey()
      onClose()
      message.success('SSH public key added successfully.')
    } catch (err: any) {
      console.error(err)
    }
  }

  return (
    <Modal
      className="w-[640px]"
      open={true}
      onClose={() => {
        onClose()
      }}
    >
      <Modal.Header>
        {isEdit ? (
          <Trans i18nKey="profile.editSshPubKeyModal.editTitle">Update SSH Key</Trans>
        ) : (
          <Trans i18nKey="profile.editSshPubKeyModal.createTitle">New SSH Key</Trans>
        )}
      </Modal.Header>
      <Modal.Body>
        <Form form={form} layout="vertical" requiredMark={false} initialValues={{ public_key: '' }}>
          <Form.Item
            name="public_key"
            className="!mb-2"
            rules={[
              { required: true, message: 'Please enter your SSH public key.' },
              {
                pattern: sshKeyPattern,
                message: 'SSH public key validity verification failed. Please check and try again.',
              },
              {
                validator: async (_: any, value: string) => {
                  try {
                    return await validateSshKeyLength(_, value)
                  } catch (error) {
                    throw new Error(error instanceof Error ? error.message : String(error))
                  }
                },
              },
            ]}
          >
            <>
              <Input.TextArea
                className="w-full h-[160px] mb-2"
                placeholder="Begins with 'ssh-dss', 'ssh-rsa', 'ecdsa-sha2-nistp256', 'ecdsa-sha2-nistp384', 'ecdsa-sha2-nistp521' or 'ssh-ed25519'"
                autoSize={false}
                allowClear
              />
            </>
          </Form.Item>

          <div className="t-lg text-secondary">
            <Trans i18nKey="profile.editSshPubKeyModal.description">
              Your SSH public key will activate within 10 minutes. Wait for system setup to
              complete.
            </Trans>
          </div>
        </Form>
      </Modal.Body>
      <div>
        <div className="pt-4 flex items-end justify-end gap-4">
          <Button
            size="md"
            type="outline"
            className="min-w-[88px]"
            onClick={() => {
              onClose()
            }}
          >
            <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
          </Button>
          <Button type="secondary" size="md" className="min-w-[88px]" onClick={onOK}>
            {isEdit ? (
              <Trans i18nKey="profile.editSshPubKeyModal.updateBtn">Update key</Trans>
            ) : (
              <Trans i18nKey="profile.editSshPubKeyModal.addBtn">Add SSH Key</Trans>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
